<template>
    <AppHead :data="data.app_head_data" />
    <LayoutHeader v-if="data.category_bidder_data.is_eventbureau" no-search box :translations :menu />
    <LayoutHeader v-else mobile-filter box :translations :menu />
    <main class="mx-auto flex max-w-[1400px] flex-col md:pt-6">
        <TopSection @go-to-matterport="() => goTo(matterport)" @go-to-location="() => goTo(location)" :translations="data.translations" :data />
        <div class="relative flex">
            <div class="flex w-full flex-1 flex-col">
                <TitleSection
                    v-if="!data.category_bidder_data.is_eventbureau"
                    :translations="data.translations"
                    :data="data"
                    :category-data="data.category_bidder_data" />
                <DirectMessageSection v-if="data?.contact_person && data.category_bidder_data" :shared-translations="translations" :data="data" />
                <DescriptionSection :translations="data.translations" :data="data" :category-data="data.category_bidder_data" />
                <BundlePriceSection v-if="data.category_bidder_data.bundle_section" :shared-translations="translations" :data="data" />
                <RoomSection v-if="data.category_bidder_data.room_section" :shared-translations="translations" :data="data" />
                <BundleEventExampleSection v-if="data.event_example_section" :translations="data.translations" :data="data.event_example_section" />
                <BundleEventServiceSection v-if="data.event_service_section" :translations="data.translations" :data="data.event_service_section" />
                <FacilitySection
                    v-if="data.facilities_section"
                    :translations="data.translations"
                    :data="data.facilities_section"
                    :category-data="data.category_bidder_data" />
                <ActivitySection
                    v-if="data.category_bidder_data.activity_section"
                    :translations="data.translations"
                    :shared-translations="translations"
                    :data="data" />
                <StatementSection v-if="data.statement_section" :translations="data.translations" :data="data.statement_section" />
                <div ref="matterport">
                    <MatterportView v-if="data.matterport_link" :translations="data.translations" :matterport_link="data.matterport_link" />
                </div>
                <NearbyAttractionSection v-if="data.place_section" :translations="data.translations" :data="data.place_section" />
                <div class="" ref="location">
                    <LocationSection v-if="data.location.render_location_section_with_marker" :data="data" />
                    <MapSection v-if="data.region_section" :translations="data.translations" :data="data.region_section" />
                </div>
                <TermsAndConditionsSection v-if="data.category_bidder_data.terms_and_conditions" :data="data" :category-data="data.category_bidder_data" />
            </div>
            <ContactBidder :translations="translations.forms" :privacyPolicy :category-data="data.category_bidder_data" :bidder="data" />
        </div>

        <LinkSection v-if="data.link_section" :data="data.link_section" />
        <Breadcrumbs v-if="data.page_breadcrumbs" :data="data.page_breadcrumbs" />

        <LayoutSection v-if="translations.help_view" curve="top" background="light">
            <HelpView :data="translations.help_view" />
        </LayoutSection>
        <AnalyticsData :data />
    </main>
</template>

<script setup lang="ts">
import AppHead from "@main/Components/AppHead/AppHead.vue";
import Breadcrumbs from "@main/Components/Breadcrumbs.vue";
import LayoutHeader from "@main/Layouts/LayoutHeader/LayoutHeader.vue";
import LayoutSection from "@main/Layouts/LayoutSection.vue";
import AnalyticsData from "@main/Pages/Bidder/Components/AnalyticsData.vue";
import DirectMessageSection from "@main/Pages/Bidder/Sections/DirectMessageSection.vue";
import HelpView from "@main/Sections/Help/HelpView.vue";
import LinkSection from "@main/Sections/LinkSection/LinkSection.vue";
import { useEndpointStore } from "@stores/endpointStore";
import { useEventStore } from "@stores/eventStore";
import { onMounted, ref } from "vue";
import ActivitySection from "./Sections/ActivitySection.vue";
import BundleEventExampleSection from "./Sections/BundleEventExampleSection.vue";
import BundleEventServiceSection from "./Sections/BundleEventServiceSection.vue";
import BundlePriceSection from "./Sections/BundlePriceSection/BundlePriceSection.vue";
import ContactBidder from "./Sections/ContactBidder.vue";
import DescriptionSection from "./Sections/DescriptionSection.vue";
import FacilitySection from "./Sections/FacilitySection/FacilitySection.vue";
import LocationSection from "./Sections/LocationSection/LocationSection.vue";
import MapSection from "./Sections/MapSection/MapSection.vue";
import MatterportView from "./Sections/MatterportView.vue";
import NearbyAttractionSection from "./Sections/NearbyAttractionSection.vue";
import RoomSection from "./Sections/RoomSection/RoomSection.vue";
import StatementSection from "./Sections/StatementSection/StatementSection.vue";
import TermsAndConditionsSection from "./Sections/TermsAndConditionsSection.vue";
import TitleSection from "./Sections/TitleSection.vue";
import TopSection from "./Sections/TopSection/TopSection.vue";

const { data } = defineProps<{
    data: App.Data.Main.Bidder.ViewModels.ShowBidderViewModel;
    menu: Domain.Shared.Data.MainMenuData;
    translations: Domain.Shared.Data.Translations;
    privacyPolicy: Domain.UI.Components.PrivacyPolicy.MainPrivacyPolicy;
}>();

useEndpointStore();
const eventStore = useEventStore();
const location = ref<HTMLElement | null>(null);
const matterport = ref<HTMLElement | null>(null);

const goTo = (element: HTMLElement | null) => {
    if (element) element?.scrollIntoView({ behavior: "smooth", block: "start" });
};

onMounted(() => {
    if (!data.category_bidder_data.is_eventbureau) eventStore.updateCategory(data.category_bidder_data.category_id);
});
</script>
